import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectMealApi, projectLogframeApi } from '../../../api/projectWorkspace';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { SectionHeader } from '../../../components/SectionHeader';
import { CommentThread } from '../../../components/CommentThread';
import { usePermission } from '../../../auth/AuthContext';
import { Project, ProjectMeal, ProjectIndicator } from '../../../types/projects';

export function ProjectMealTab({ project }: { project: Project }) {
  const canManage = usePermission('project_meal.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ['project-meal', project.id], queryFn: () => projectMealApi.get(project.id) });
  const { register, handleSubmit } = useForm<Partial<ProjectMeal>>({ values: query.data?.data });

  const saveMutation = useMutation({
    mutationFn: (values: Partial<ProjectMeal>) => projectMealApi.update(project.id, values),
    onSuccess: (result) => { showSuccess('Monitoring plan saved.'); queryClient.setQueryData(['project-meal', project.id], { data: result.data }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  if (query.isLoading) return <p className="muted">Loading…</p>;

  return (
    <>
      <form onSubmit={handleSubmit((values) => saveMutation.mutate(values))}>
        <section className="card">
          <h2>Monitoring Plan</h2>
          <p className="muted">The approach to monitoring this project. Indicator-level targets, baselines, and tracking come from the Logical Framework and are shown below.</p>
          <label>Monitoring Approach<textarea rows={2} {...register('monitoring_approach')} disabled={!canManage} /></label>
          <label>Baseline Approach<textarea rows={2} {...register('baseline_approach')} disabled={!canManage} /></label>
          <label>Endline Approach<textarea rows={2} {...register('endline_approach')} disabled={!canManage} /></label>
          <label>Evaluation Approach<textarea rows={2} {...register('evaluation_approach')} disabled={!canManage} /></label>
          <label>Data Sources<textarea rows={2} {...register('data_sources')} disabled={!canManage} /></label>
          <label>Verification Methods<textarea rows={2} {...register('verification_methods')} disabled={!canManage} /></label>
          <label>
            Reporting Frequency
            <select {...register('reporting_frequency')} disabled={!canManage}>
              <option value="">- not set -</option>
              <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
              <option value="biannual">Biannual</option><option value="annual">Annual</option><option value="end_of_project">End of Project</option>
            </select>
          </label>
          <label>Learning Approach<textarea rows={2} {...register('learning_approach')} disabled={!canManage} /></label>
          <label>Key MEAL Assumptions<textarea rows={2} {...register('key_assumptions')} disabled={!canManage} /></label>
          <CommentThread projectId={project.id} entityType="meal" />
        </section>
        {canManage && <div className="form-actions"><button type="submit" className="btn-primary" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save'}</button></div>}
      </form>

      <IndicatorTrackingSheet projectId={project.id} />
    </>
  );
}

function IndicatorTrackingSheet({ projectId }: { projectId: number }) {
  const canManage = usePermission('project_meal.manage');
  const { showError } = useToast();
  const queryClient = useQueryClient();

  const indicatorsQuery = useQuery({ queryKey: ['project-indicators-all', projectId], queryFn: () => projectLogframeApi.listIndicatorsForProject(projectId) });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: { actualValue?: string | null; trackingStatus?: string | null; dateOfVerification?: string | null } }) =>
      projectLogframeApi.updateIndicatorActual(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-indicators-all', projectId] }),
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  const indicators = indicatorsQuery.data?.data ?? [];

  return (
    <div className="panel">
      <SectionHeader title="Indicator Tracking Sheet" />
      <div className="panel-body">
      <p className="muted">Every indicator here is extracted from the Logical Framework - add or edit indicators there, not here.</p>

      {indicators.length === 0 && <p className="muted">No indicators defined yet. Add them under the Logical Framework tab.</p>}

      {indicators.length > 0 && (
        <div className="gantt-scroll">
          <table className="budget-table indicator-tracking-table">
            <thead>
              <tr>
                <th>Code</th><th>Indicator</th><th>Outcome / Output</th><th>Baseline</th><th>Target</th>
                <th>Current Achievement</th><th>Achievement %</th><th>Means of Verification</th><th>Data Source</th>
                <th>Frequency</th><th>Responsible Person</th><th>Status</th><th>Date of Verification</th><th>Comments</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((ind) => (
                <IndicatorRow
                  key={ind.id}
                  indicator={ind}
                  projectId={projectId}
                  canManage={canManage}
                  onSave={(input) => updateMutation.mutate({ id: ind.id, input })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
}

const STATUS_LABELS: Record<string, string> = { not_started: 'Not Started', on_track: 'On Track', off_track: 'Off Track', achieved: 'Achieved' };

function IndicatorRow({
  indicator, projectId, canManage, onSave,
}: {
  indicator: ProjectIndicator;
  projectId: number;
  canManage: boolean;
  onSave: (input: { actualValue?: string | null; trackingStatus?: string | null; dateOfVerification?: string | null }) => void;
}) {
  const target = indicator.target_total ?? (indicator.target_value ? Number(indicator.target_value) : null);
  const actual = indicator.actual_value ? Number(indicator.actual_value) : null;
  const achievementPct = target && !Number.isNaN(target) && target > 0 && actual !== null && !Number.isNaN(actual)
    ? Math.round((actual / target) * 100)
    : null;

  function commit(overrides: Partial<{ actualValue: string; trackingStatus: string; dateOfVerification: string }>) {
    onSave({
      actualValue: overrides.actualValue ?? indicator.actual_value ?? null,
      trackingStatus: overrides.trackingStatus ?? indicator.tracking_status ?? null,
      dateOfVerification: overrides.dateOfVerification ?? indicator.date_of_verification ?? null,
    });
  }

  return (
    <tr>
      <td><span className="code-badge code-badge-indicator">{indicator.indicator_code}</span></td>
      <td>{indicator.name}</td>
      <td className="muted">Outcome {indicator.outcome_code} / Output {indicator.output_code}</td>
      <td>{indicator.baseline_value ?? '-'}</td>
      <td>{indicator.target_value ?? indicator.target_total ?? '-'}{indicator.unit_of_measurement ? ` ${indicator.unit_of_measurement}` : ''}</td>
      <td>
        <input
          className="indicator-actual-input"
          defaultValue={indicator.actual_value ?? ''}
          disabled={!canManage}
          onBlur={(e) => { if (e.target.value !== (indicator.actual_value ?? '')) commit({ actualValue: e.target.value }); }}
        />
      </td>
      <td>{achievementPct !== null ? `${achievementPct}%` : '-'}</td>
      <td className="muted indicator-mov-cell">{indicator.means_of_verification ?? '-'}</td>
      <td className="muted">{indicator.data_source ?? '-'}</td>
      <td className="muted">{indicator.frequency ?? '-'}</td>
      <td className="muted">{indicator.responsible_person_name ?? '-'}</td>
      <td>
        <select
          className="indicator-status-select"
          defaultValue={indicator.tracking_status ?? ''}
          disabled={!canManage}
          onChange={(e) => commit({ trackingStatus: e.target.value })}
        >
          <option value="">- not set -</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </td>
      <td>
        <input
          type="date"
          className="indicator-date-input"
          defaultValue={indicator.date_of_verification ?? ''}
          disabled={!canManage}
          onBlur={(e) => { if (e.target.value !== (indicator.date_of_verification ?? '')) commit({ dateOfVerification: e.target.value }); }}
        />
      </td>
      <td><CommentThread projectId={projectId} entityType="indicator" entityId={indicator.id} /></td>
    </tr>
  );
}
